import { MapPin, Phone, Clock, ExternalLink, Star } from "lucide-react";
import { Link } from "react-router-dom";

const LOCATIONS = [
  {
    id: "memphis",
    name: "Memphis · Original",
    badge: "Since 2021",
    addr1: "3711 Lamar Avenue",
    addr2: "Memphis, TN 38118",
    phone: "(901) 623-7196",
    phoneHref: "tel:9016237196",
    mapsQuery: "3711+Lamar+Avenue+Memphis+TN+38118",
    hours: ["Mon – Fri  10A – 10P", "Saturday  12P – 10P", "Sunday     Closed"],
    testId: "loc-memphis",
  },
  {
    id: "west-memphis",
    name: "West Memphis · Arkansas",
    badge: "New · Summer 2025",
    addr1: "109 S Worthington Drive",
    addr2: "West Memphis, AR 72301",
    phone: "(870) 394-4229",
    phoneHref: "tel:8703944229",
    mapsQuery: "109+S+Worthington+Drive+West+Memphis+AR+72301",
    hours: ["Mon – Fri  10A – 10P", "Saturday  12P – 10P", "Sunday     Closed"],
    testId: "loc-west-memphis",
    landingTo: "/west-memphis",
  },
];

export const Visit = () => (
  <section id="visit" data-testid="visit-section" className="bg-[#0A0A0A] py-20 md:py-28 border-t border-[#222]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-12 max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-3">Find Us</div>
        <h2 className="font-heading text-5xl sm:text-6xl text-white mb-4">
          Two Spots · <span className="text-[#E60000]">One Family</span>
        </h2>
        <p className="text-white/70 leading-relaxed">
          Walk in, call ahead, or tap the DoorDash button to get it delivered. We're at both
          Memphis &amp; West Memphis, ready when you are.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {LOCATIONS.map((loc) => (
          <article
            key={loc.id}
            data-testid={loc.testId}
            className="bg-[#141414] border-2 border-[#333] hover:border-[#E60000] transition-colors overflow-hidden flex flex-col"
          >
            <div className="aspect-video bg-[#1A1A1A] relative">
              <iframe
                title={`${loc.name} map`}
                src={`https://www.google.com/maps?q=${loc.mapsQuery}&output=embed`}
                className="w-full h-full grayscale-[80%] contrast-125"
                loading="lazy"
                allowFullScreen
              />
              <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-[#E60000] text-white px-2 py-1 text-[10px] uppercase tracking-widest font-bold">
                <Star className="w-3 h-3" strokeWidth={3} /> {loc.badge}
              </div>
            </div>

            <div className="p-5 md:p-6 flex-1 flex flex-col">
              <h3 className="font-heading text-3xl text-white mb-4">{loc.name}</h3>

              <div className="grid sm:grid-cols-2 gap-4 mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-[#E60000]" strokeWidth={2.5} />
                    <span className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Address</span>
                  </div>
                  <div className="text-sm text-white/85 font-mono leading-tight">
                    <div>{loc.addr1}</div>
                    <div>{loc.addr2}</div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4 text-[#E60000]" strokeWidth={2.5} />
                    <span className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Phone</span>
                  </div>
                  <a
                    href={loc.phoneHref}
                    data-testid={`${loc.testId}-phone`}
                    className="text-sm text-white/85 font-mono hover:text-[#E60000]"
                  >
                    {loc.phone}
                  </a>
                </div>
                <div className="sm:col-span-2">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-[#E60000]" strokeWidth={2.5} />
                    <span className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Hours</span>
                  </div>
                  <ul className="text-sm text-white/85 font-mono leading-tight space-y-0.5">
                    {loc.hours.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </div>
              </div>

              <div className="mt-auto flex flex-wrap gap-2 pt-4 border-t border-[#222]">
                <a
                  href={`https://maps.google.com/?daddr=${loc.mapsQuery}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${loc.testId}-directions`}
                  className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-4 h-10 uppercase tracking-widest text-[10px] font-bold border-2 border-transparent hover:border-white transition-colors"
                >
                  <MapPin className="w-3 h-3" strokeWidth={3} /> Directions
                </a>
                {loc.landingTo && (
                  <Link
                    to={loc.landingTo}
                    data-testid={`${loc.testId}-landing`}
                    className="inline-flex items-center gap-2 bg-transparent border-2 border-[#333] hover:border-[#E60000] text-white px-4 h-10 uppercase tracking-widest text-[10px] font-bold transition-colors"
                  >
                    Learn More <ExternalLink className="w-3 h-3" strokeWidth={3} />
                  </Link>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);
